import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, PackagePlus, X, Plus, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail, inr } from "@/lib/api";
import { QuoteCard } from "@/components/BookingFlow";
import { COUNTRIES } from "@/lib/countries";
import { useEffect } from "react";



const EMPTY_CONSIGNEE = { name: "", phone: "", email: "", address_line_1: "", address_line_2: "", city: "", state: "", postcode: "" };

const Field = ({ label, value, onChange, ph, type = "text", testid, required = true, max }) => (
  <div>
    <label className="text-[10px] font-mono text-zinc-400 uppercase">{label}</label>
    <input type={type} required={required} maxLength={max} value={value} onChange={onChange} placeholder={ph}
      data-testid={testid} min={type === "number" ? "0.01" : undefined} step={type === "number" ? "0.01" : undefined}
      className="mt-1 w-full border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
  </div>
);

export default function IntlBooking() {
  const navigate = useNavigate();
  const [q, setQ] = useState({ pickup_pincode: "", destination_country: "US", destination_postcode: "", weight_kg: "0.5" });
  const [quotes, setQuotes] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [countryQuery, setCountryQuery] = useState("");
  const [bk, setBk] = useState({
    warehouse_id: "",
    consignee: EMPTY_CONSIGNEE,
    items: [{ name: "", qty: "1", unit_price_inr: "" }],
    dims: { weight_kg: "0.5", length_cm: "20", breadth_cm: "15", height_cm: "10" },
  });

  useEffect(() => {
    appApi.get("warehouses").then((d) => setWarehouses(d.warehouses || [])).catch(() => {});
  }, []);

  const set = (k) => (e) => setQ((f) => ({ ...f, [k]: e.target.value }));
  const setC = (k) => (e) => setBk((s) => ({ ...s, consignee: { ...s.consignee, [k]: e.target.value } }));
  const setD = (k) => (e) => setBk((s) => ({ ...s, dims: { ...s.dims, [k]: e.target.value } }));
  const setItem = (i, k) => (e) =>
    setBk((s) => ({ ...s, items: s.items.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it)) }));

  const getQuotes = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setQuotes(null);
    setBooking(null);
    try {
      const d = await appApi.post("rates/international", {
        pickup_pincode: q.pickup_pincode,
        destination_country: q.destination_country,
        destination_postcode: q.destination_postcode,
        weight_kg: parseFloat(q.weight_kg),
      });
      setQuotes(d.quotes || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const openBooking = (quote) => {
    const wh = warehouses.find((w) => w.pincode === q.pickup_pincode) || warehouses[0];
    setBooking(quote);
    setBookingError("");
    setBk((s) => ({
      ...s,
      warehouse_id: wh?.id || "",
      consignee: { ...EMPTY_CONSIGNEE, postcode: q.destination_postcode },
      dims: { weight_kg: q.weight_kg, length_cm: "20", breadth_cm: "15", height_cm: "10" },
    }));
  };

  const confirmBooking = async (e) => {
    e.preventDefault();
    setBookingError("");
    setBusy(true);
    try {
      const payload = {
        warehouse_id: bk.warehouse_id,
        sr_courier_id: booking.sr_courier_id,
        expected_rate: booking.rate,
        consignee: {
          name: bk.consignee.name,
          phone: bk.consignee.phone,
          email: bk.consignee.email,
          address_line_1: bk.consignee.address_line_1,
          address_line_2: bk.consignee.address_line_2,
          city: bk.consignee.city,
          state: bk.consignee.state,
          country_code: q.destination_country,
          postcode: bk.consignee.postcode,
        },
        items: bk.items.map((it) => ({
          name: it.name,
          qty: parseInt(it.qty) || 1,
          unit_price_inr: parseFloat(it.unit_price_inr),
        })),
        weight_kg: parseFloat(bk.dims.weight_kg),
        length_cm: parseFloat(bk.dims.length_cm),
        breadth_cm: parseFloat(bk.dims.breadth_cm),
        height_cm: parseFloat(bk.dims.height_cm),
      };
      const d = await appApi.post("orders/international", payload);
      const awb = d.awb || d.order?.awb || "";
      toast.success(`International shipment booked${awb ? ` — AWB ${awb}` : ""}. Wallet debited.`);
      navigate("/dashboard/orders");
    } catch (err) {
      setBookingError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="intl-booking">
      <form onSubmit={getQuotes} data-testid="intl-quote-form" className="border-2 border-obsidian bg-white p-6 grid sm:grid-cols-4 gap-4 items-end">
        <div>
          <label htmlFor="intl-pickup" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Pickup pincode (IN)</label>
          <input id="intl-pickup" data-testid="intl-pickup-input" required maxLength={6} value={q.pickup_pincode} onChange={set("pickup_pincode")} placeholder="713101"
            className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
        </div>
        <div>
          <label htmlFor="intl-country" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Destination country</label>
          <input data-testid="intl-country-search" value={countryQuery} onChange={(e) => setCountryQuery(e.target.value)}
            placeholder={`Search ${COUNTRIES.length} countries…`}
            className="mt-2 w-full border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
          <select id="intl-country" data-testid="intl-country-select" value={q.destination_country} onChange={set("destination_country")}
            className="mt-2 w-full border-2 border-obsidian bg-paper px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian">
            {COUNTRIES.filter((c) => c.name.toLowerCase().includes(countryQuery.toLowerCase())).map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
          <p className="text-[10px] font-mono text-zinc-400 mt-1 uppercase tracking-wider" data-testid="intl-country-selected">
            Selected: {COUNTRIES.find((c) => c.code === q.destination_country)?.name || q.destination_country}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="intl-postcode" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Postcode</label>
            <input id="intl-postcode" data-testid="intl-postcode-input" required value={q.destination_postcode} onChange={set("destination_postcode")} placeholder="10001"
              className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
          </div>
          <div>
            <label htmlFor="intl-weight" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Weight (kg)</label>
            <input id="intl-weight" data-testid="intl-weight-input" required type="number" min="0.05" step="0.05" value={q.weight_kg} onChange={set("weight_kg")}
              className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
          </div>
        </div>
        <button type="submit" data-testid="intl-quote-submit" disabled={loading}
          className="inline-flex items-center justify-center gap-2 bg-obsidian text-sky-300 font-bold px-6 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#2563EB] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
          <Globe className="w-4 h-4" /> {loading ? "Fetching…" : "Get Quotes"}
        </button>
      </form>

      {error && <div data-testid="intl-error" className="mt-6 border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3">{error}</div>}

      {quotes && (
        <div data-testid="intl-quotes" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {quotes.map((quote) => <QuoteCard key={quote.courier_id} q={quote} onBook={openBooking} booking={booking} />)}
          {quotes.length === 0 && <p className="text-sm text-zinc-500 sm:col-span-2">No international couriers for this lane right now.</p>}
        </div>
      )}

      {booking && (
        <form onSubmit={confirmBooking} data-testid="intl-booking-form" className="mt-6 border-2 border-obsidian bg-white shadow-[8px_8px_0px_0px_#2563EB]">
          <div className="flex items-center justify-between px-6 py-4 border-b-2 border-obsidian bg-obsidian text-white">
            <p className="font-bold text-sm">
              <Globe className="w-4 h-4 inline mr-2 text-sky-300" />
              <span className="text-sky-300">{booking.courier_name}</span> · {inr(booking.rate)} · IN {q.pickup_pincode} → {q.destination_country} {q.destination_postcode}
              <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">International · Prepaid · ETA {booking.eta_days}</span>
            </p>
            <button type="button" onClick={() => setBooking(null)} aria-label="Close booking" data-testid="intl-booking-close" className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 grid lg:grid-cols-2 gap-6">
            {bookingError && (
              <div data-testid="intl-booking-error" className="lg:col-span-2 border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3">{bookingError}</div>
            )}

            <div className="space-y-5">
              <div>
                <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Pickup warehouse</p>
                <select value={bk.warehouse_id} onChange={(e) => setBk((s) => ({ ...s, warehouse_id: e.target.value }))} required
                  data-testid="intl-warehouse-select"
                  className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian">
                  {warehouses.length === 0 && <option value="">No warehouses — add one first</option>}
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{w.address_title} — {w.city} {w.pincode}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Package dimensions</p>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  <Field label="Wt kg" type="number" value={bk.dims.weight_kg} ph="0.5" testid="intl-dim-weight" onChange={setD("weight_kg")} />
                  <Field label="L cm" type="number" value={bk.dims.length_cm} ph="20" testid="intl-dim-length" onChange={setD("length_cm")} />
                  <Field label="B cm" type="number" value={bk.dims.breadth_cm} ph="15" testid="intl-dim-breadth" onChange={setD("breadth_cm")} />
                  <Field label="H cm" type="number" value={bk.dims.height_cm} ph="10" testid="intl-dim-height" onChange={setD("height_cm")} />
                </div>
              </div>
              <div>
                <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Items (customs declaration)</p>
                <div className="space-y-2 mt-2" data-testid="intl-items">
                  {bk.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end border border-zinc-300 p-3">
                      <div className="col-span-6">
                        <Field label="Item name" value={it.name} ph="Cotton shirt" testid={`intl-item-${i}-name`} onChange={setItem(i, "name")} />
                      </div>
                      <div className="col-span-2">
                        <Field label="Qty" type="number" value={it.qty} ph="1" testid={`intl-item-${i}-qty`} onChange={setItem(i, "qty")} />
                      </div>
                      <div className="col-span-3">
                        <Field label="Unit ₹" type="number" value={it.unit_price_inr} ph="500" testid={`intl-item-${i}-price`} onChange={setItem(i, "unit_price_inr")} />
                      </div>
                      <div className="col-span-1">
                        {bk.items.length > 1 && (
                          <button type="button" aria-label="Remove item" data-testid={`intl-item-${i}-remove`}
                            onClick={() => setBk((s) => ({ ...s, items: s.items.filter((_, j) => j !== i) }))}
                            className="p-2 border-2 border-obsidian text-red-600 hover:bg-red-50 w-full flex justify-center">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <button type="button" data-testid="intl-add-item"
                    onClick={() => setBk((s) => ({ ...s, items: [...s.items, { name: "", qty: "1", unit_price_inr: "" }] }))}
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest border-2 border-obsidian px-4 py-2 hover:bg-acid hover:text-white transition-colors duration-150">
                    <Plus className="w-3.5 h-3.5" /> Add item
                  </button>
                </div>
              </div>
            </div>

            <div>
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Consignee (receiver)</p>
              <div className="space-y-3 mt-2">
                <Field label="Full name" value={bk.consignee.name} ph="John Doe" testid="intl-consignee-name" onChange={setC("name")} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Phone" value={bk.consignee.phone} ph="+1…" testid="intl-consignee-phone" onChange={setC("phone")} />
                  <Field label="Email (optional)" required={false} value={bk.consignee.email} ph="john@mail.com" testid="intl-consignee-email" onChange={setC("email")} />
                </div>
                <Field label="Address line 1" value={bk.consignee.address_line_1} ph="123 Main St" testid="intl-consignee-address1" onChange={setC("address_line_1")} />
                <Field label="Address line 2 (optional)" required={false} value={bk.consignee.address_line_2} ph="Apt 4B" testid="intl-consignee-address2" onChange={setC("address_line_2")} />
                <div className="grid grid-cols-3 gap-3">
                  <Field label="City" value={bk.consignee.city} ph="New York" testid="intl-consignee-city" onChange={setC("city")} />
                  <Field label="State (optional)" required={false} value={bk.consignee.state} ph="NY" testid="intl-consignee-state" onChange={setC("state")} />
                  <Field label="Postcode" value={bk.consignee.postcode} ph="10001" testid="intl-consignee-postcode" onChange={setC("postcode")} />
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t-2 border-obsidian flex items-center justify-between flex-wrap gap-3">
            <p className="font-mono text-xs text-zinc-500">
              Wallet will be debited <span className="font-bold text-white bg-acid px-2 py-0.5 border border-obsidian">{inr(booking.rate)}</span> · PREPAID · customs duties may apply at destination
            </p>
            <button type="submit" disabled={busy} data-testid="intl-booking-submit"
              className="inline-flex items-center gap-2 bg-acid text-white font-bold px-8 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
              <PackagePlus className="w-4 h-4" /> {busy ? "Booking…" : "Confirm International Booking"}
            </button>
          </div>
        </form>
      )}

      <p className="text-xs font-mono text-zinc-400 uppercase tracking-widest mt-6 flex items-center gap-2">
        <Star className="w-3.5 h-3.5" /> International shipments are prepaid — customs docs generated from your items list
      </p>
    </div>
  );
}
